/**
 * AgentDashboardPage Phase 3 R22: tabbed page at /agent.
 *
 * Tabs (URL-driven via ?tab=orders | ?tab=clinics | ?tab=retribution,
 * default orders):
 *   - Orders: mounts the shared OrderTrackerPage with scope="agent" and
 *     withShell=false (so we don't double-wrap AppShell). RLS narrows the
 *     visible orders to clinics introduced by this agent.
 *   - Your clinics: the YourClinicsStrip lives inside its own tab,
 *     listing the agent's introduced clinics by name.
 *   - Retribution: the real commission UI. KPI strip, monthly stacked
 *     chart, per-clinic eligibility cards, and a month-scoped drill-down
 *     table. All numbers come from the @insolech/sdk agentCommission*
 *     query factories (RLS narrows rows to this agent).
 *
 * The legacy /agent/orders route now redirects to /agent?tab=orders so any
 * existing bookmarks keep working.
 */

import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Eyebrow,
  Heading,
  KPICard,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@insolech/ui';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  AreaChart,
  Area,
} from 'recharts';
import {
  Building2,
  Calendar,
  LineChart as LineChartIcon,
  Wallet,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import {
  agentCommissionSummaryQuery,
  agentCommissionMonthlyQuery,
  agentClinicTimelineQuery,
  agentCommissionLinesQuery,
  agentCommissionRatesQuery,
  agentClinicAgreementsQuery,
  type CommissionBucket,
  type AgentCommissionMonthlyRow,
  type AgentClinicTimelineRow,
  type AgentCommissionRateRow,
  type AgentClinicAgreementRateRow,
} from '@insolech/sdk';
import { AppShell } from '@/components/layout/AppShell';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';
import { OrderTrackerPage } from '@/features/orders/OrderTrackerPage';
import {
  ColumnFilterPopover,
  type ColumnFilterOption,
} from '@/features/orders/ColumnFilterPopover';

interface CapturedClinic {
  id: string;
  name: string;
  city: string | null;
  emirate: string | null;
}

type AgentTab = 'orders' | 'clinics' | 'retribution';

const VALID_TABS: ReadonlyArray<AgentTab> = ['orders', 'clinics', 'retribution'];

function isAgentTab(v: string | null | undefined): v is AgentTab {
  return v != null && (VALID_TABS as ReadonlyArray<string>).includes(v);
}

export function AgentDashboardPage() {
  const { user, fullName } = useAuth();
  const { t } = useTranslation(['common']);
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab');
  const tab: AgentTab = isAgentTab(rawTab) ? rawTab : 'orders';
  const setTab = (next: AgentTab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const clinicsQuery = useQuery({
    queryKey: ['agent', 'captured-clinics', user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<CapturedClinic[]> => {
      const { data, error } = await supabase
        .from('clinics')
        .select('id, name, city, emirate')
        .eq('acquired_by_agent_id', user!.id)
        .order('name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const firstName = fullName ? fullName.split(' ')[0] : '';

  return (
    <AppShell>
      <main className="px-6 py-5 max-w-[1400px] mx-auto space-y-5">
        <header>
          <Eyebrow>Agent</Eyebrow>
          <Heading as="h1" size="h3" className="mt-2">
            Welcome back{firstName ? `, ${firstName}` : ''}.
          </Heading>
          <p className="text-text-secondary mt-1 text-sm">
            Read-only view of the clinics you introduced to INSOLECH, the
            orders flowing through them, and your commission.
          </p>
        </header>

        <Tabs
          value={tab}
          onValueChange={(v) => setTab(isAgentTab(v) ? v : 'orders')}
        >
          <TabsList>
            <TabsTrigger value="orders" data-testid="agent-tab-orders">
              {t('common:agent_tab_orders', { defaultValue: 'Orders' })}
            </TabsTrigger>
            <TabsTrigger value="clinics" data-testid="agent-tab-clinics">
              {t('common:agent_tab_clinics', { defaultValue: 'Your clinics' })}
            </TabsTrigger>
            <TabsTrigger value="retribution" data-testid="agent-tab-retribution">
              {t('common:agent_tab_retribution', { defaultValue: 'Retribution' })}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="orders">
            {/* withShell=false so we don't double-wrap AppShell. The
                OrderTrackerPage keeps its own Eyebrow + Heading ("Orders
                from your clinics") so the tab body has its own context
                strip distinct from the page header above. */}
            <OrderTrackerPage scope="agent" withShell={false} />
          </TabsContent>
          <TabsContent value="clinics">
            <div className="space-y-3">
              <div>
                <Heading as="h2" size="h5">
                  Your clinics
                </Heading>
                <p className="text-text-secondary mt-1 text-sm">
                  Clinics you introduced to INSOLECH.
                </p>
              </div>
              <YourClinicsCards
                loading={clinicsQuery.isLoading}
                clinics={clinicsQuery.data ?? []}
                agentId={user?.id ?? null}
              />
            </div>
          </TabsContent>
          <TabsContent value="retribution">
            <RetributionTab agentId={user?.id ?? null} />
          </TabsContent>
        </Tabs>
      </main>
    </AppShell>
  );
}

/**
 * YourClinicsCards big-card layout for the agent's introduced clinics
 * (Phase 3 R23 feedback, replaces the old YourClinicsStrip pill row). For
 * each captured clinic we render a single Card with:
 *   - Header: clinic name, city/emirate sub-line, eyebrow tag
 *   - Body: a compact rate table (only rates with target_commission_aed > 0;
 *     'none' bucket skipped) sourced from the shared 8-row rate card
 *   - Footer: total accrued AED from agentClinicTimelineQuery (rendered
 *     once timeline data is ready; skipped silently while loading)
 *
 * We deliberately reuse the agent's clinicsQuery (already loaded for the
 * captured-clinic list) and add two read-only queries the page didn't have
 * here before: the shared rate table (same rows for every clinic) and the
 * per-clinic timeline (for accrued totals only). Both queries are cheap and
 * cached against the React Query cache the retribution tab already uses.
 */
function YourClinicsCards({
  loading,
  clinics,
  agentId,
}: {
  loading: boolean;
  clinics: ReadonlyArray<CapturedClinic>;
  agentId: string | null;
}) {
  const ratesQuery = useQuery({
    ...agentCommissionRatesQuery(supabase),
    enabled: !!agentId,
  });
  const timelineQuery = useQuery({
    ...agentClinicTimelineQuery(supabase, { agentId }),
    enabled: !!agentId,
  });

  const timelineByClinic = React.useMemo(() => {
    const map = new Map<string, AgentClinicTimelineRow>();
    for (const row of timelineQuery.data ?? []) {
      map.set(row.clinic_id, row);
    }
    return map;
  }, [timelineQuery.data]);

  if (loading) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-[320px] w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (clinics.length === 0) {
    return (
      <Card>
        <CardBody className="px-6 py-5">
          <EmptyState
            icon={<Building2 className="h-6 w-6" />}
            title="No clinics yet"
            description="When an admin links a clinic to your account, it will appear here."
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <div
      role="list"
      aria-label="Your clinics"
      className="grid gap-4 lg:grid-cols-2"
    >
      {clinics.map((c) => (
        <ClinicCard
          key={c.id}
          clinic={c}
          timeline={timelineByClinic.get(c.id) ?? null}
          rates={ratesQuery.data ?? []}
          ratesLoading={ratesQuery.isLoading}
          ratesError={ratesQuery.error}
          agentId={agentId}
        />
      ))}
    </div>
  );
}

/**
 * ClinicCard , single card in the Your Clinics grid.
 *
 * Per-card it fetches agentClinicAgreementsQuery (the agent's own
 * deal-sheet rows for this clinic from
 * agent_clinic_commission_agreements; RLS narrows to agent_id =
 * auth.uid(), admins still see everything). Each bucket row in
 * BUCKET_ORDER is then resolved as: agreement when present, else the
 * global rate card from agent_commission_rates. The card surfaces the
 * negotiated unit price and per-unit commission with a "Negotiated"
 * badge on agreement rows and a "Default" badge on rate-card fallbacks.
 *
 * NOTE: clinic_commission_overrides used to be merged in here too, but
 * the user spec for the "Your clinics" tab is explicit: show
 * "negotiated value OR default value" only. Overrides remain in scope
 * for backend accrual (calc_commission) and for the admin clinic-detail
 * page, but they no longer drive the columns rendered below.
 *
 * A separate hook per card keeps each card's data lifecycle
 * independent: when an admin updates an agreement for clinic A, only
 * card A refetches and the other cards stay rendered. The query
 * factory key is per (agent, clinic) so React Query handles cache
 * scoping automatically.
 */
function ClinicCard({
  clinic,
  timeline,
  rates,
  ratesLoading,
  ratesError,
  agentId,
}: {
  clinic: CapturedClinic;
  timeline: AgentClinicTimelineRow | null;
  rates: ReadonlyArray<AgentCommissionRateRow>;
  ratesLoading: boolean;
  ratesError: unknown;
  agentId: string | null;
}) {
  const agreementsQuery = useQuery({
    ...agentClinicAgreementsQuery(supabase, {
      agentId,
      clinicId: clinic.id,
    }),
    enabled: !!agentId,
  });

  const ratesByBucket = React.useMemo(() => {
    const m = new Map<CommissionBucket, AgentCommissionRateRow>();
    for (const r of rates) m.set(r.bucket, r);
    return m;
  }, [rates]);

  const agreementsByBucket = React.useMemo(() => {
    const m = new Map<CommissionBucket, AgentClinicAgreementRateRow>();
    for (const a of agreementsQuery.data ?? []) m.set(a.bucket, a);
    return m;
  }, [agreementsQuery.data]);

  // Build the per-clinic effective rate rows. For each active bucket in
  // BUCKET_ORDER we prefer the negotiated agreement; otherwise the
  // global rate card. Buckets with neither row (e.g. a race window
  // during the v2 migration apply) are skipped silently so the card
  // never renders an empty placeholder.
  const effectiveRows = React.useMemo(() => {
    const out: Array<{
      bucket: Exclude<CommissionBucket, 'none'>;
      negotiated_price_aed: number;
      commission_aed: number;
      isNegotiated: boolean;
    }> = [];
    for (const bucket of BUCKET_ORDER) {
      const agreement = agreementsByBucket.get(bucket);
      if (agreement) {
        out.push({
          bucket,
          negotiated_price_aed: agreement.agreed_price_aed,
          commission_aed: agreement.commission_aed,
          isNegotiated: true,
        });
        continue;
      }
      const rate = ratesByBucket.get(bucket);
      if (rate) {
        out.push({
          bucket,
          negotiated_price_aed: Number(rate.target_price_aed ?? 0),
          commission_aed: Number(rate.target_commission_aed ?? 0),
          isNegotiated: false,
        });
      }
    }
    return out;
  }, [agreementsByBucket, ratesByBucket]);

  const subLine = [clinic.city, clinic.emirate].filter(Boolean).join(', ');
  const dataLoading = ratesLoading || agreementsQuery.isLoading;
  const dataError = ratesError ?? agreementsQuery.error;

  return (
    <Card role="listitem">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <Eyebrow>Acquired clinic</Eyebrow>
            <CardTitle className="mt-1 text-lg flex items-center gap-2">
              <Building2
                className="h-5 w-5 text-text-muted shrink-0"
                aria-hidden="true"
              />
              <span className="truncate">{clinic.name}</span>
            </CardTitle>
            {subLine ? (
              <p className="text-xs text-text-muted mt-1">{subLine}</p>
            ) : null}
          </div>
          <Badge variant="brand" size="sm">
            Commission eligible
          </Badge>
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {dataLoading ? (
          <Skeleton className="h-32 w-full rounded-md" />
        ) : dataError ? (
          <Alert tone="error">
            {(dataError as Error)?.message ?? 'Could not load rates.'}
          </Alert>
        ) : effectiveRows.length === 0 ? (
          <p className="text-xs text-text-muted">
            No commission products configured yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-end">Negotiated price (AED)</TableHead>
                  <TableHead className="text-end">Commission (AED)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {effectiveRows.map((r) => (
                  <TableRow key={r.bucket}>
                    <TableCell className="text-text-primary">
                      {BUCKET_LABELS[r.bucket] ?? r.bucket}
                    </TableCell>
                    <TableCell className="text-end tabular">
                      {formatAed(r.negotiated_price_aed)}
                    </TableCell>
                    <TableCell className="text-end tabular font-medium">
                      {formatAed(r.commission_aed)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <p className="text-xs text-text-muted">
          Commission applies to orders from this clinic for 12 months after
          its first submission. Negotiated rows reflect the deal you have
          on file for this clinic; default rows fall back to the global
          rate card.
        </p>
        {timeline ? (
          <div className="border-t border-border-subtle pt-3 text-xs text-text-muted flex items-center justify-between gap-2">
            <span>Accrued to date</span>
            <span className="text-sm font-medium text-text-primary tabular">
              {formatAed(timeline.total_accrued_aed)}
            </span>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Retribution tab                                                           */
/* -------------------------------------------------------------------------- */

/**
 * AED currency formatter shared by every card / row / tooltip in the
 * retribution surface. 'en-AE' delivers the right grouping separator and
 * spells out AED as the unit. Two decimal places is the standard for AED.
 */
const aedFormatter = new Intl.NumberFormat('en-AE', {
  style: 'currency',
  currency: 'AED',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatAed(n: number | null | undefined): string {
  return aedFormatter.format(Number(n ?? 0));
}

/**
 * BUCKET_COLORS deliberately maps every commission bucket to a Tailwind
 * token color so the chart palette stays consistent with the rest of the
 * design system. Hex values are read off the existing tokens so we don't
 * pull theme JS into the chart at runtime.
 */
/**
 * BUCKET_COLORS chart palette per commission bucket. The 6 custom_cns_*
 * shades sit in the brand-purple family so the stacked bar groups them
 * visually as "custom-made insoles"; sandal/fito stay green-ish
 * (premium products); ready_made_* uses the warm warning/clay/sand
 * palette to read as "off-the-shelf". Legacy v1 buckets are kept so any
 * pre-v2 monthly aggregate row still renders with a real colour.
 */
const BUCKET_COLORS: Record<Exclude<CommissionBucket, 'none'>, string> = {
  // Custom-Made Insoles (CNS) , brand purple shades
  custom_cns_tpu: '#6E59A5', // brand-500
  custom_cns_eva: '#7c63b5', // brand-500/+10
  custom_cns_pa: '#8d75c5',
  custom_cns_pp: '#9b87f5', // brand-300
  custom_cns_fg: '#5a47a0',
  custom_cns_cf: '#43356f',
  // Sandals + FITO , greens
  sandal: '#10B981', // success-500
  fito: '#059669', // success-700
  // Ready-mades , warm palette
  ready_made_cape: '#F59E0B', // warning-500
  ready_made_ca2e: '#fbbf24',
  ready_made_dahe: '#0EA5E9', // info-500
  ready_made_kik2: '#B7510E', // clay-500
  ready_made_clob: '#A1846A', // sand-500
  // Legacy buckets , kept so pre-v2 aggregates still chart cleanly
  custom_insole: '#6E59A5',
  ready_made_calca: '#F59E0B',
  ready_made_kid: '#0EA5E9',
  ready_made_clever: '#B7510E',
  ready_made_haglund: '#A1846A',
};

const BUCKET_LABELS: Record<Exclude<CommissionBucket, 'none'>, string> = {
  custom_cns_tpu: 'Custom CNS (TPU)',
  custom_cns_eva: 'Custom CNS (EVA)',
  custom_cns_pa: 'Custom CNS (PA12)',
  custom_cns_pp: 'Custom CNS (PP)',
  custom_cns_fg: 'Custom CNS (Fibreglass)',
  custom_cns_cf: 'Custom CNS (Carbon Fibre)',
  sandal: 'Sandal',
  ready_made_cape: 'Ready-made CALCA PRO X',
  ready_made_ca2e: 'Ready-made CALCA II',
  ready_made_dahe: 'Ready-made DAILY',
  ready_made_kik2: 'Ready-made KID CONTROL',
  ready_made_clob: 'Ready-made CLEVER soft',
  fito: 'FITO shoe',
  // Legacy
  custom_insole: 'Custom insole (legacy)',
  ready_made_calca: 'Ready-made calca (legacy)',
  ready_made_kid: 'Ready-made kid (legacy)',
  ready_made_clever: 'Ready-made clever (legacy)',
  ready_made_haglund: 'Ready-made haglund (legacy)',
};

/**
 * BUCKET_ORDER is the canonical render order used by:
 *   , the stacked monthly chart (Bar dataKey loop),
 *   , the rate card in the Your Clinics card,
 *   , the per-clinic effective rate table.
 * Order follows the product family hierarchy: custom_cns_* first
 * (6 materials), then sandal, then ready_made_* (5 families), then
 * fito. Legacy v1 buckets are intentionally omitted because they
 * have no rate row after the v2 migration; if a historical aggregate
 * row references one it falls through to BUCKET_LABELS via the chart
 * formatter, not the order map.
 */
const BUCKET_ORDER: ReadonlyArray<Exclude<CommissionBucket, 'none'>> = [
  'custom_cns_tpu',
  'custom_cns_eva',
  'custom_cns_pa',
  'custom_cns_pp',
  'custom_cns_fg',
  'custom_cns_cf',
  'sandal',
  'ready_made_cape',
  'ready_made_ca2e',
  'ready_made_dahe',
  'ready_made_kik2',
  'ready_made_clob',
  'fito',
];

/**
 * Format a YYYY-MM string into a short month label suitable for X axis
 * ticks ("Jan", "Feb") and dropdown options ("Jan 2026"). Falls back to
 * the raw string if parsing fails so we never render "NaN".
 */
function formatPeriodShort(period: string): string {
  const [y, m] = period.split('-').map((s) => Number(s));
  if (!y || !m || Number.isNaN(y) || Number.isNaN(m)) return period;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString('en-US', { month: 'short' });
}

function formatPeriodLong(period: string): string {
  const [y, m] = period.split('-').map((s) => Number(s));
  if (!y || !m || Number.isNaN(y) || Number.isNaN(m)) return period;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
}

function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
}

function currentMonthString(now: Date = new Date()): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/**
 * RetributionTab top-level container. Owns the queries (so each sub-card
 * can be a presentational component). Renders the header, the KPI strip,
 * the drill-down (Commission lines), the clinic timeline, and the monthly
 * chart in vertical order. The drill-down no longer takes a "month" filter;
 * the user filters via per-column popovers on the commission lines table.
 */
function RetributionTab({ agentId }: { agentId: string | null }) {
  const summary = useQuery({
    ...agentCommissionSummaryQuery(supabase, { agentId }),
    enabled: !!agentId,
  });
  const monthly = useQuery({
    ...agentCommissionMonthlyQuery(supabase, { agentId }),
    enabled: !!agentId,
  });
  const timeline = useQuery({
    ...agentClinicTimelineQuery(supabase, { agentId }),
    enabled: !!agentId,
  });
  const rates = useQuery({
    ...agentCommissionRatesQuery(supabase),
    enabled: !!agentId,
  });

  return (
    <div className="space-y-5">
      <div>
        <Heading as="h2" size="h5">
          Retribution
        </Heading>
        <p className="text-text-secondary mt-1 text-sm">
          Variable commission on products sold by clinics you introduced.
          Eligibility runs for 12 months from each clinic&apos;s first
          order. Confirmed once the order reaches production.
        </p>
        <div className="mt-2">
          <HowItWorksDisclosure
            loading={rates.isLoading}
            error={rates.error}
            rates={rates.data ?? []}
          />
        </div>
      </div>

      <RetributionKPIStrip
        loading={summary.isLoading}
        error={summary.error}
        data={summary.data ?? null}
      />

      <RetributionDrillDown agentId={agentId} />

      <RetributionClinicTimeline
        loading={timeline.isLoading}
        error={timeline.error}
        rows={timeline.data ?? []}
      />

      <RetributionMonthlyChart
        loading={monthly.isLoading}
        error={monthly.error}
        rows={monthly.data ?? []}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

/**
 * RetryAlert tiny inline error card that invalidates the queries it owns
 * on click. We deliberately avoid toasts here: this surface is read-only
 * and an inline retry keeps the failing card in context.
 */
function RetryAlert({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Alert
      tone="error"
      title="Could not load"
      actions={
        <Button type="button" variant="ghost" size="sm" onClick={onRetry}>
          Try again
        </Button>
      }
    >
      {message}
    </Alert>
  );
}

function RetributionKPIStrip({
  loading,
  error,
  data,
}: {
  loading: boolean;
  error: unknown;
  data: {
    this_month_accrued_aed: number;
    this_month_confirmed_aed: number;
    pending_unpaid_aed: number;
    paid_12mo_aed: number;
  } | null;
}) {
  const queryClient = useQueryClient();

  if (loading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[112px] rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <RetryAlert
        message={(error as Error)?.message ?? 'Failed to load summary.'}
        onRetry={() =>
          queryClient.invalidateQueries({
            queryKey: ['agent-commissions', 'summary'],
          })
        }
      />
    );
  }

  const accrued = data?.this_month_accrued_aed ?? 0;
  const confirmed = data?.this_month_confirmed_aed ?? 0;
  const pending = data?.pending_unpaid_aed ?? 0;
  const paid12 = data?.paid_12mo_aed ?? 0;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <KPICard
        label="This month accrued"
        value={formatAed(accrued)}
        icon={<LineChartIcon className="h-4 w-4" />}
        accentColor="brand"
      />
      <KPICard
        label={
          <span className="flex flex-col gap-0.5">
            <span>This month confirmed</span>
            <span className="text-[10px] font-normal normal-case tracking-normal text-text-muted">
              of {formatAed(accrued)} accrued
            </span>
          </span>
        }
        value={formatAed(confirmed)}
        icon={<CheckCircle2 className="h-4 w-4" />}
        accentColor="success"
      />
      <KPICard
        label="Pending payouts"
        value={formatAed(pending)}
        icon={<Clock className="h-4 w-4" />}
        accentColor="warning"
      />
      <KPICard
        label="Paid (12 months)"
        value={formatAed(paid12)}
        icon={<Wallet className="h-4 w-4" />}
        accentColor="info"
      />
    </div>
  );
}

/**
 * MonthlyTotalTooltip , custom recharts Tooltip that collapses every
 * stacked bucket into a single AED total for the hovered month. The
 * per-bucket breakdown lives in the Commission lines table above, so the
 * chart only needs to communicate the month-level magnitude.
 */
function MonthlyTotalTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce(
    (acc, p) => acc + (typeof p.value === 'number' ? p.value : 0),
    0,
  );
  return (
    <div className="rounded-md border border-border-subtle bg-surface-elevated px-3 py-2 shadow-md">
      <div className="text-xs text-text-muted">
        {label ? formatPeriodLong(label) : ''}
      </div>
      <div className="text-sm font-semibold text-text-primary">
        {formatAed(total)}
      </div>
    </div>
  );
}

/**
 * BRAND_500_HEX , single source of truth for the chart bar color. Mirrors
 * `--color-brand-500` from packages/ui/src/styles/globals.css. We hardcode
 * the hex instead of reading the CSS variable because recharts <Bar fill>
 * does not resolve `var(...)` at SVG render time across all browsers.
 */
const BRAND_500_HEX = '#26657a';

/**
 * Advance a YYYY-MM string by N calendar months (UTC). Used to roll the
 * 12-month forward window forward from the start month.
 */
function addMonths(period: string, n: number): string {
  const [y, m] = period.split('-').map((s) => Number(s));
  if (!y || !m || Number.isNaN(y) || Number.isNaN(m)) return period;
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  const yy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${yy}-${mm}`;
}

function RetributionMonthlyChart({
  loading,
  error,
  rows,
}: {
  loading: boolean;
  error: unknown;
  rows: ReadonlyArray<AgentCommissionMonthlyRow>;
}) {
  const queryClient = useQueryClient();

  // Collapse (period, bucket, total) into one row per month with a single
  // 'total' key (sum of all non-'none' buckets for that month). The chart
  // renders a single bar per month, so per-bucket coloring is no longer
  // needed; the drill-down table above already breaks the month down by
  // bucket.
  const chartData = React.useMemo(() => {
    const byPeriod = new Map<string, number>();
    for (const r of rows) {
      if (r.bucket === 'none') continue;
      const prev = byPeriod.get(r.period) ?? 0;
      byPeriod.set(r.period, prev + Number(r.total_aed ?? 0));
    }
    return byPeriod;
  }, [rows]);

  // Build the 12-month forward window. The x-axis starts at the FIRST
  // month with any data (or the current month if no data exists anywhere)
  // and extends forward 11 more months, so the trailing forecast stays
  // visible even when those buckets are empty.
  const currentPeriod = currentMonthString();
  const startPeriod = React.useMemo(() => {
    if (chartData.size === 0) return currentPeriod;
    // Earliest period that has any commission accrued.
    const periods = Array.from(chartData.keys()).sort();
    return periods[0] ?? currentPeriod;
  }, [chartData, currentPeriod]);

  const forwardData = React.useMemo(() => {
    const out: Array<{ period: string; total: number }> = [];
    for (let i = 0; i < 12; i++) {
      const p = addMonths(startPeriod, i);
      out.push({ period: p, total: chartData.get(p) ?? 0 });
    }
    return out;
  }, [chartData, startPeriod]);

  const allZero = React.useMemo(() => {
    return forwardData.every((d) => d.total === 0);
  }, [forwardData]);

  // Y-axis: always start at 0, tick every 50 AED, axis max is the smallest
  // multiple of 250 that is >= the largest bar (minimum 250 even on empty).
  const { axisMax, ticks } = React.useMemo(() => {
    const maxVal = Math.max(0, ...forwardData.map((d) => d.total));
    const max = Math.max(250, Math.ceil(maxVal / 250) * 250);
    const ts: number[] = [];
    for (let v = 0; v <= max; v += 50) ts.push(v);
    return { axisMax: max, ticks: ts };
  }, [forwardData]);

  const subtitle = React.useMemo(() => {
    return `12-month forecast from ${formatPeriodLong(startPeriod)}`;
  }, [startPeriod]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <LineChartIcon className="h-5 w-5 text-text-muted" aria-hidden="true" />
          Monthly commission
        </CardTitle>
        <p className="text-xs text-text-muted mt-0.5">{subtitle}</p>
      </CardHeader>
      <CardBody>
        {loading ? (
          <Skeleton className="h-[320px] w-full rounded-md" />
        ) : error ? (
          <RetryAlert
            message={(error as Error)?.message ?? 'Failed to load chart.'}
            onRetry={() =>
              queryClient.invalidateQueries({
                queryKey: ['agent-commissions', 'monthly'],
              })
            }
          />
        ) : allZero ? (
          <EmptyState
            icon={<LineChartIcon className="h-6 w-6" />}
            title="No data"
            description="When commission accrues on orders from your clinics, it will appear here."
          />
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={forwardData}
              margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
            >
              <XAxis
                dataKey="period"
                tickFormatter={formatPeriodShort}
                tick={{ fontSize: 12, fill: 'currentColor' }}
                className="text-text-muted"
              />
              <YAxis
                tick={{ fontSize: 12, fill: 'currentColor' }}
                className="text-text-muted"
                ticks={ticks}
                domain={[0, axisMax]}
                tickFormatter={(v: number) =>
                  new Intl.NumberFormat('en-US', {
                    notation: 'compact',
                    maximumFractionDigits: 1,
                  }).format(Number(v))
                }
              />
              <Tooltip content={<MonthlyTotalTooltip />} />
              {/* Single bar per month showing the AED total. Per-bucket
                  breakdown lives in the Commission lines table above. */}
              <Bar dataKey="total" fill={BRAND_500_HEX} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardBody>
    </Card>
  );
}

function RetributionClinicTimeline({
  loading,
  error,
  rows,
}: {
  loading: boolean;
  error: unknown;
  rows: ReadonlyArray<AgentClinicTimelineRow>;
}) {
  const queryClient = useQueryClient();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Calendar className="h-5 w-5 text-text-muted" aria-hidden="true" />
          Your clinics&apos; commission windows
        </CardTitle>
        <p className="text-xs text-text-muted mt-0.5">
          Eligibility runs for 12 months from each clinic&apos;s first
          submitted order.
        </p>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[160px] rounded-md" />
            ))}
          </div>
        ) : error ? (
          <RetryAlert
            message={(error as Error)?.message ?? 'Failed to load timeline.'}
            onRetry={() =>
              queryClient.invalidateQueries({
                queryKey: ['agent-commissions', 'clinic-timeline'],
              })
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-6 w-6" />}
            title="No clinics yet"
            description="Your introduced clinics will appear here once an admin links them."
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((c) => (
              <ClinicTimelineCard key={c.clinic_id} row={c} />
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function ClinicTimelineCard({ row }: { row: AgentClinicTimelineRow }) {
  const truncated =
    row.clinic_name.length > 28
      ? `${row.clinic_name.slice(0, 27)}…`
      : row.clinic_name;
  const closed =
    row.first_submitted_at != null &&
    (row.months_remaining == null || row.months_remaining <= 0);
  const sparkData = row.monthly_sparkline.map((p) => ({
    period: p.period,
    total: p.total_aed,
  }));
  return (
    <div
      className="rounded-md border border-border-subtle bg-surface-elevated p-4 flex flex-col gap-2"
      title={row.clinic_name}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium text-sm text-text-primary truncate">
          {truncated}
        </div>
        {row.first_submitted_at == null ? (
          <Badge variant="muted" size="sm">
            No orders yet
          </Badge>
        ) : closed ? (
          <Badge variant="error" size="sm">
            Window closed
          </Badge>
        ) : (
          <Badge variant="success" size="sm">
            {row.months_remaining ?? 0} mo left
          </Badge>
        )}
      </div>
      <div className="text-xs text-text-muted space-y-0.5">
        <div>First order: {formatDateShort(row.first_submitted_at)}</div>
        <div>Eligibility ends: {formatDateShort(row.eligibility_end_at)}</div>
      </div>
      <div className="text-sm font-medium text-text-primary tabular">
        {formatAed(row.total_accrued_aed)}
        <span className="ms-1 text-xs font-normal text-text-muted">
          accrued
        </span>
      </div>
      {sparkData.length > 1 ? (
        <div className="h-10 -mx-1">
          <ResponsiveContainer width="100%" height={40}>
            <AreaChart data={sparkData}>
              <Area
                dataKey="total"
                type="monotone"
                stroke={BUCKET_COLORS.custom_insole}
                fill={BUCKET_COLORS.custom_insole}
                fillOpacity={0.18}
                strokeWidth={1.5}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Page-size options for the Commission lines drill-down. Controls how
 * many rows are FETCHED from the backend per page; the visible window
 * stays capped at 7-8 rows via the Table's wrapperClassName + sticky
 * thead pattern (see CardBody below), regardless of what is fetched.
 */
const RETRIBUTION_PAGE_SIZE_OPTIONS = [25, 50, 75, 100, 150, 200] as const;
const DEFAULT_RETRIBUTION_PAGE_SIZE = 25;

/**
 * Sortable string columns on the commission lines table. Each one is
 * filterable via a ColumnFilterPopover. Bucket + Payout + Status are
 * synthetic (computed off the row) so we keep their value extractors
 * inline rather than in a generic per-column dictionary.
 */
type CommissionFilterColumn = 'clinic' | 'sku' | 'bucket' | 'status' | 'payout';

/** Status pill values shown in the Status column. */
const STATUS_VALUES = ['Confirmed', 'Pending'] as const;
/** Payout pill values shown in the Payout column. Mirrors the badge
 *  computation in the row renderer. */
const PAYOUT_VALUES = [
  'Paid',
  'Pending payout',
  'Awaiting batch',
  'Not yet confirmed',
] as const;

function RetributionDrillDown({ agentId }: { agentId: string | null }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-stateful page-size selector. The `csize` param lives alongside the
  // tab param the page already uses. We validate against the whitelist so
  // a stale URL value never reaches the SDK.
  const pageSizeFromUrl = Number(searchParams.get('csize')) || 0;
  const pageSize: number = (RETRIBUTION_PAGE_SIZE_OPTIONS as ReadonlyArray<number>).includes(
    pageSizeFromUrl,
  )
    ? pageSizeFromUrl
    : DEFAULT_RETRIBUTION_PAGE_SIZE;

  // Column filters stored in the URL under `cfilters` as a JSON object
  // mapping column key to selected value array (mirrors the OrderTracker
  // `filters` param). Empty object = no filter active.
  const columnFilters = React.useMemo<Record<string, string[]>>(() => {
    const raw = searchParams.get('cfilters');
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      const out: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (Array.isArray(v) && v.length > 0) {
          out[k] = (v as unknown[]).map(String);
        }
      }
      return out;
    } catch {
      return {};
    }
  }, [searchParams]);
  const filtersActive = Object.keys(columnFilters).length > 0;

  const setColumnFilter = (col: CommissionFilterColumn, values: string[]) => {
    const next = { ...columnFilters };
    if (values.length === 0) delete next[col];
    else next[col] = values;
    const serialized = Object.keys(next).length === 0 ? null : JSON.stringify(next);
    const params = new URLSearchParams(searchParams);
    if (serialized == null) params.delete('cfilters');
    else params.set('cfilters', serialized);
    setSearchParams(params, { replace: true });
  };

  const setPageSize = (next: number) => {
    const params = new URLSearchParams(searchParams);
    if (next === DEFAULT_RETRIBUTION_PAGE_SIZE) params.delete('csize');
    else params.set('csize', String(next));
    setSearchParams(params, { replace: true });
  };

  // Single fetch sized by the page-size selector. No server-side period
  // narrowing now that the Month dropdown is gone; the user filters via
  // the per-column popovers (Clinic / SKU / Bucket / Status / Payout)
  // applied client-side to the rows we just fetched.
  const linesQuery = useQuery({
    ...agentCommissionLinesQuery(supabase, {
      agentId,
      page: 0,
      pageSize,
    }),
    enabled: !!agentId,
  });

  const rows = linesQuery.data?.rows ?? [];
  const total = linesQuery.data?.total ?? 0;

  // Synthetic per-row values used both for the popover option lists and
  // the active-filter evaluation. Kept in one place so they stay in sync.
  const rowStatus = (r: (typeof rows)[number]): string =>
    r.is_confirmed ? 'Confirmed' : 'Pending';
  const rowPayout = (r: (typeof rows)[number]): string => {
    if (r.payout?.status === 'paid') return 'Paid';
    if (r.payout?.status === 'pending') return 'Pending payout';
    if (r.is_confirmed) return 'Awaiting batch';
    return 'Not yet confirmed';
  };
  const rowBucketLabel = (r: (typeof rows)[number]): string => {
    if (r.bucket === 'none') return 'None';
    return (
      BUCKET_LABELS[r.bucket as Exclude<CommissionBucket, 'none'>] ?? r.bucket
    );
  };

  const FILTER_EXTRACTORS: Record<
    CommissionFilterColumn,
    (r: (typeof rows)[number]) => string | null
  > = {
    clinic: (r) => r.clinic?.name ?? null,
    sku: (r) => r.catalog_sku ?? null,
    bucket: (r) => rowBucketLabel(r),
    status: (r) => rowStatus(r),
    payout: (r) => rowPayout(r),
  };

  // Filter the fetched rows by every active column filter (AND across
  // columns, OR within a single column's selected values). When no
  // filter is active we render the rows as-is.
  const filteredRows = React.useMemo(() => {
    if (!filtersActive) return rows;
    const entries = Object.entries(columnFilters) as Array<
      [CommissionFilterColumn, string[]]
    >;
    return rows.filter((r) =>
      entries.every(([col, values]) => {
        const extractor = FILTER_EXTRACTORS[col];
        if (!extractor) return true;
        const v = extractor(r);
        return v != null && values.includes(v);
      }),
    );
    // FILTER_EXTRACTORS is recreated each render but only references stable
    // module-level helpers; including it would invalidate the memo every
    // render. The same pattern is used in OrderTrackerPage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, columnFilters, filtersActive]);

  // Distinct option list per filterable column, derived from the loaded
  // rows. Status + Payout + Bucket use a fixed value list so the popover
  // shows every possible value even when the loaded page only contains
  // some of them.
  const optionsForLoaded = (col: CommissionFilterColumn): ColumnFilterOption[] => {
    const extractor = FILTER_EXTRACTORS[col];
    const seen = new Set<string>();
    const out: ColumnFilterOption[] = [];
    for (const r of rows) {
      const v = extractor(r);
      if (v == null || v === '') continue;
      if (seen.has(v)) continue;
      seen.add(v);
      out.push({ value: v, label: v });
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
  };

  const statusOptions: ColumnFilterOption[] = STATUS_VALUES.map((s) => ({
    value: s,
    label: s,
  }));
  const payoutOptions: ColumnFilterOption[] = PAYOUT_VALUES.map((p) => ({
    value: p,
    label: p,
  }));
  const bucketOptions: ColumnFilterOption[] = BUCKET_ORDER.map((b) => ({
    value: BUCKET_LABELS[b] ?? b,
    label: BUCKET_LABELS[b] ?? b,
  }));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-5 w-5 text-text-muted" aria-hidden="true" />
              Commission lines
            </CardTitle>
            <p className="text-xs text-text-muted mt-0.5">
              Each row is one product on one order, with the accrued
              commission and current status. Filter any column from its
              header.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardBody className="px-0 pb-0">
        {linesQuery.isLoading ? (
          <div className="space-y-2 px-6 pb-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        ) : linesQuery.error ? (
          <div className="px-6 pb-4">
            <RetryAlert
              message={
                (linesQuery.error as Error)?.message ??
                'Failed to load commission lines.'
              }
              onRetry={() =>
                queryClient.invalidateQueries({
                  queryKey: ['agent-commissions', 'lines'],
                })
              }
            />
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 pb-4">
            <EmptyState
              icon={<Calendar className="h-6 w-6" />}
              title="No commission lines yet"
              description="Lines accrue when an order from one of your clinics is submitted."
            />
          </div>
        ) : (
          <>
            {/* Visible-window cap: ~7-8 rows then internal scroll, sticky
                thead so the column headers stay pinned. Mirrors the
                OrderTrackerPage pattern (wrapperClassName max-h + sticky
                TableHeader). The h-24rem cap matches roughly 8 rows at
                the table's default row height. */}
            <Table wrapperClassName="max-h-[24rem] overflow-auto">
              <TableHeader className="sticky top-0 z-20 bg-sand-50 shadow-[0_1px_0_0_var(--color-border-subtle)]">
                <TableRow>
                  <TableHead>Order date</TableHead>
                  <TableHead>
                    <ColumnFilterPopover
                      label="Clinic"
                      sortActive={false}
                      sortDirection="asc"
                      onSortAsc={() => undefined}
                      onSortDesc={() => undefined}
                      options={optionsForLoaded('clinic')}
                      selected={columnFilters.clinic ?? []}
                      onSelectedChange={(next) => setColumnFilter('clinic', next)}
                      searchPlaceholder="Search clinic"
                    />
                  </TableHead>
                  <TableHead>
                    <ColumnFilterPopover
                      label="SKU"
                      sortActive={false}
                      sortDirection="asc"
                      onSortAsc={() => undefined}
                      onSortDesc={() => undefined}
                      options={optionsForLoaded('sku')}
                      selected={columnFilters.sku ?? []}
                      onSelectedChange={(next) => setColumnFilter('sku', next)}
                      searchPlaceholder="Search SKU"
                    />
                  </TableHead>
                  <TableHead>
                    <ColumnFilterPopover
                      label="Bucket"
                      sortActive={false}
                      sortDirection="asc"
                      onSortAsc={() => undefined}
                      onSortDesc={() => undefined}
                      options={bucketOptions}
                      selected={columnFilters.bucket ?? []}
                      onSelectedChange={(next) => setColumnFilter('bucket', next)}
                      searchPlaceholder="Search bucket"
                    />
                  </TableHead>
                  <TableHead className="text-end">Qty</TableHead>
                  <TableHead className="text-end">Unit price</TableHead>
                  <TableHead className="text-end">Commission</TableHead>
                  <TableHead>
                    <ColumnFilterPopover
                      label="Status"
                      sortActive={false}
                      sortDirection="asc"
                      onSortAsc={() => undefined}
                      onSortDesc={() => undefined}
                      options={statusOptions}
                      selected={columnFilters.status ?? []}
                      onSelectedChange={(next) => setColumnFilter('status', next)}
                      searchPlaceholder="Search status"
                    />
                  </TableHead>
                  <TableHead>
                    <ColumnFilterPopover
                      label="Payout"
                      sortActive={false}
                      sortDirection="asc"
                      onSortAsc={() => undefined}
                      onSortDesc={() => undefined}
                      options={payoutOptions}
                      selected={columnFilters.payout ?? []}
                      onSelectedChange={(next) => setColumnFilter('payout', next)}
                      searchPlaceholder="Search payout"
                    />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <div className="px-2 py-6">
                        <EmptyState
                          icon={<Calendar className="h-6 w-6" />}
                          title="No matches"
                          description="Try clearing one of the active column filters."
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((r) => {
                    const orderDate = r.order?.submitted_at ?? r.accrued_at;
                    const payoutLabel = rowPayout(r);
                    const payoutVariant:
                      | 'default'
                      | 'muted'
                      | 'brand'
                      | 'success'
                      | 'warning'
                      | 'error'
                      | 'info'
                      | 'outline' =
                      payoutLabel === 'Paid'
                        ? 'success'
                        : payoutLabel === 'Pending payout'
                          ? 'warning'
                          : payoutLabel === 'Awaiting batch'
                            ? 'info'
                            : 'muted';
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="text-text-secondary">
                          {formatDateShort(orderDate)}
                        </TableCell>
                        <TableCell className="text-text-primary">
                          {r.clinic?.name ?? '-'}
                        </TableCell>
                        <TableCell className="text-text-secondary font-mono text-xs">
                          {r.catalog_sku ?? '-'}
                        </TableCell>
                        <TableCell className="text-text-secondary">
                          {rowBucketLabel(r)}
                        </TableCell>
                        <TableCell className="text-end tabular">
                          {r.qty}
                        </TableCell>
                        <TableCell className="text-end tabular">
                          {formatAed(r.unit_price_aed)}
                        </TableCell>
                        <TableCell className="text-end tabular font-medium">
                          {formatAed(r.line_commission_aed)}
                        </TableCell>
                        <TableCell>
                          {r.is_confirmed ? (
                            <Badge variant="success" size="sm">
                              Confirmed
                            </Badge>
                          ) : (
                            <Badge variant="warning" size="sm">
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={payoutVariant} size="sm">
                            {payoutLabel}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between border-t border-border-subtle px-4 py-3 text-sm">
              <label className="flex items-center gap-2 text-text-secondary">
                <span>Rows per page</span>
                <select
                  value={pageSize}
                  aria-label="Rows per page"
                  data-testid="retribution-page-size-select"
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="h-8 rounded-md border border-border-default bg-surface-elevated px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  {RETRIBUTION_PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <span className="text-text-muted">
                {filtersActive
                  ? `${filteredRows.length} matching of ${rows.length} fetched (${total} total)`
                  : `${rows.length} of ${total} total`}
              </span>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}


function HowItWorksDisclosure({
  loading,
  error,
  rates,
}: {
  loading: boolean;
  error: unknown;
  rates: ReadonlyArray<AgentCommissionRateRow>;
}) {
  // The rate table is small (8 rows) and not load-bearing, so a native
  // <details> disclosure keeps the markup accessible (ARIA expanded state
  // is handled by the browser) without adding a modal dependency.
  return (
    <details className="group rounded-md border border-border-subtle bg-surface-elevated">
      <summary className="cursor-pointer list-none px-3 py-2 text-xs font-medium text-text-secondary flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5">
          <LineChartIcon className="h-3.5 w-3.5" aria-hidden="true" />
          How it works
        </span>
        <span className="text-text-muted group-open:hidden">Show</span>
        <span className="text-text-muted hidden group-open:inline">Hide</span>
      </summary>
      <div className="border-t border-border-subtle px-3 py-3 text-xs text-text-secondary space-y-2">
        <p>
          Each product category has a target price and a floor price. You
          earn a fixed commission at the target price; below the floor,
          commission is zero. Between the floor and the target the
          commission scales linearly.
        </p>
        {loading ? (
          <Skeleton className="h-32 w-full rounded-md" />
        ) : error ? (
          <Alert tone="error">
            {(error as Error)?.message ?? 'Could not load rates.'}
          </Alert>
        ) : rates.length === 0 ? (
          <p className="text-text-muted">No rate card configured yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-end">Target price</TableHead>
                  <TableHead className="text-end">Floor price</TableHead>
                  <TableHead className="text-end">
                    Commission at target
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rates.map((r) => (
                  <TableRow key={r.bucket}>
                    <TableCell className="text-text-primary">
                      {r.bucket === 'none'
                        ? 'None'
                        : (BUCKET_LABELS[
                            r.bucket as Exclude<CommissionBucket, 'none'>
                          ] ?? r.bucket)}
                    </TableCell>
                    <TableCell className="text-end tabular">
                      {formatAed(r.target_price_aed)}
                    </TableCell>
                    <TableCell className="text-end tabular">
                      {formatAed(r.floor_price_aed)}
                    </TableCell>
                    <TableCell className="text-end tabular">
                      {formatAed(r.target_commission_aed)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </details>
  );
}
